import { IonList } from '@ionic/react';
import { Form, Formik } from 'formik';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Tenant, useGetTenantQuery } from '../../api/graphql/generated';
import { useHasFeature } from '../../api/hooks/feature.hooks';
import { useUpdateTenant } from '../../api/hooks/tenant.hooks';
import { trackSetTenantName } from '../../config/analytics';
import { Partners } from '../../config/branding';
import { useRedirect } from '../../pages/redirect.hooks';
import { logger } from '../../util/logger';
import { useGetPartner, useSetPartnerLazy } from '../branding/branding.hooks';
import { FormSubmitButton } from '../form/form-submit.button';
import {
  IonFormPicklistField,
  PicklistOption,
} from '../form/ion-form-picklist.field';
import { IonFormField } from '../form/ion-form.field';
import { SaveStatusToast } from '../toast/toast.gql';

const tenantSchema = yup.object().shape({
  name: yup.string().nullable().required('*'),
});

type TenantForm = Partial<Tenant> & { partnerName: string };

const TenantAdministration = (): ReactElement => {
  const { i18n } = useTranslation();
  const [updateTenant, { loading, error }] = useUpdateTenant();
  const redirect = useRedirect();
  const { data } = useGetTenantQuery();

  const partner = useGetPartner();
  const setPartner = useSetPartnerLazy();

  const hasPartnerCustomization = useHasFeature<boolean>(
    'beacon-partner-customization'
  );

  const initialValues = useMemo<TenantForm>(() => {
    const {
      __typename,
      id: _id,
      ...tenant
    } = data?.getTenant ?? ({} as Partial<Tenant>);

    return {
      ...tenant,
      partnerName: partner.name,
    };
  }, [data?.getTenant, partner]);

  const onSubmit = async (input: TenantForm) => {
    const {
      planId: _planId,
      active: _active,
      partnerName,
      ...propsToSave
    } = input;

    try {
      await updateTenant({
        variables: {
          input: propsToSave,
        },
      });

      if (initialValues.partnerName !== partnerName) {
        setPartner(partnerName);
      }

      trackSetTenantName();
      redirect();
      logger.info('Successfully updated tenant record!');
    } catch (error) {
      logger.error('Error updating tenant record', { error, input });
    }
  };

  const partnerOptions = useMemo<PicklistOption[]>(() => {
    return Partners.map((p) => {
      return {
        label: i18n.t(p.label),
        value: p.name,
      };
    });
  }, [i18n]);

  return (
    <>
      <Formik
        validationSchema={tenantSchema}
        onSubmit={onSubmit}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="tenant-form">
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:tenant_name')}
                  name="name"
                  inputProps={{
                    type: 'text',
                    autocorrect: 'on',
                    autocapitalize: 'on',
                  }}
                />
                {hasPartnerCustomization && (
                  <IonFormPicklistField
                    name="partnerName"
                    label={i18n.t('common:partner')}
                    options={partnerOptions}
                  />
                )}
              </IonList>
              <br />
              <FormSubmitButton
                loading={loading}
                disabled={!isValid || !dirty}
              />
            </Form>
          );
        }}
      </Formik>
      <SaveStatusToast loading={loading} error={error} />
    </>
  );
};

export { TenantAdministration };
